import i18n, { type InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { appConfig } from 'shared';
import { z } from 'zod';
import { env } from '~/env';
import { locales } from '~/lib/i18n-locales';

/** i18n options: bundled preload for the default language, HTTP-loaded namespaces for the rest. */
const initOptions: InitOptions = {
  resources: locales, // Preload default ('en') translations
  debug: env.VITE_DEBUG_I18N,
  ns: ['c', 'about', 'error', 'appError'],
  partialBundledLanguages: true,
  supportedLngs: appConfig.languages,
  load: 'languageOnly',
  fallbackLng: appConfig.defaultLanguage,
  interpolation: {
    escapeValue: false, // React escapes by default
  },
  react: { useSuspense: false },
  defaultNS: 'c',
  // Locale files are flat, and a dot belongs to the key (`docs` and `docs.openapi_specification` are both keys)
  keySeparator: false,
  backend: {
    // Processed namespaces (common + app merged into `c`), served by the vite plugin in dev and as build assets.
    loadPath: '/locales/{{lng}}/{{ns}}.json',
  },
};

const instance = i18n.use(Backend).use(LanguageDetector).use(initReactI18next);

// The page declares the language its text is in, so screen readers pronounce it right; an unsupported pick falls back
instance.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') document.documentElement.lang = instance.resolvedLanguage ?? lng;
});

instance.init(initOptions);

// The validator's own text for a wrong email only says it is invalid; ours shows the expected form. A message set on a schema still wins.
z.config({ customError: (issue) => (issue.code === 'invalid_format' && issue.format === 'email' ? i18n.t('error:invalid_email') : undefined) });

// HMR for lazy-loaded locales (non-bundled languages and HTTP-loaded namespaces)
if (import.meta.hot) {
  import.meta.hot.on('i18next-hmr:update', () => {
    i18n.reloadResources().then(() => {
      i18n.emit('languageChanged', i18n.language);
    });
  });
}
